import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Stack, Box, ListItemText, Link, List, Grid, MobileStepper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SwipeableViews from 'react-swipeable-views';
import { autoPlay } from 'react-swipeable-views-utils';
import { amplitude } from 'wisestep-api';
import { Button, WsButton, WsColors, WsTypography } from 'wisestep-base';

import { ActivateFeature } from '../../../components/ActivateFeature';
import { useWorkspaceContext } from '../../../context';
import CalenderSVG from '../../../images/content/Calender.svg';
import DragandDropSVG from '../../../images/content/DragandDrop.svg';
import ExtensionchromeSVG from '../../../images/content/Extensionchrome.svg';
import NoCandidatesSVG from '../../../images/content/NoCandidates.svg';
import NoJobsSVG from '../../../images/content/NoJobs.svg';
import NoSmtpSVG from '../../../images/content/NoSMTP.svg';
import NoCandidateIcon from '../../../images/NoCandidate.svg';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';

interface IPositionsProps {
  height?: number;
  children: React.ReactNode;
}

const Positions = ({ children, height }: IPositionsProps) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ justifyContent: 'center' }}
      spacing={2}
      height={height}
    >
      {children}
    </Stack>
  );
};

interface ISwipeableGuideOptions {
  onStepChange: (args: number) => void;
}

export const SwipeableGuideOptions = ({ onStepChange }: ISwipeableGuideOptions) => {
  const workspace = useWorkspaceContext();
  const navigate = useNavigate();
  const options = [
    {
      component: (
        <Box>
          <>
            <Positions height={120}>
              <img src={NoJobsSVG} alt="No Jobs ?" height={130} width={140} />
              <Box>
                <WsTypography bold>First things first...lets post your first job</WsTypography>
                <WsTypography bold>
                  Create your first job and market it by publishing on other job boards
                </WsTypography>
              </Box>
            </Positions>
            <Stack sx={{ justifyContent: 'center' }} alignItems="center">
              <ActivateFeature feature="JS_INTERNAL_JOB_POSTING">
                <WsButton
                  onClick={() => {
                    amplitude('job_create_dashboard', {
                      clickedOn: 'new job',
                      pageName: 'Dashboard',
                      pageUrl: window.location.href,
                      workspaceName: workspace.name,
                      planType: workspace.planType,
                      interfaceType: 'Web',
                    });
                    navigate('/jobs/new');
                  }}
                >
                  Post a new Job
                </WsButton>
              </ActivateFeature>
            </Stack>
          </>
        </Box>
      ),
    },
    {
      component: (
        <Box>
          <>
            <Positions height={100}>
              <img src={NoCandidatesSVG} alt="No Candidate ?" height={130} width={140} />
              <Box>
                <List sx={{ width: '100%', maxWidth: 440, bgcolor: 'background.paper' }}>
                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    primary="Wisestep extracts candidate information and helps you quickly"
                  />
                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    primary="find best-matched profiles using boolean or keyword search."
                  />
                </List>
              </Box>
            </Positions>
            <Stack sx={{ justifyContent: 'center' }} alignItems="center" pt={3}>
              <WsButton onClick={() => navigate('/candidates/')}>Build your database</WsButton>
            </Stack>
          </>
        </Box>
      ),
    },
    {
      component: (
        <Box>
          <>
            <Positions height={100}>
              <img src={NoSmtpSVG} alt="No Email ?" height={130} width={140} />
              <Box>
                <List sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}>
                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    primary="Connect your email account and send emails to "
                  />
                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    primary=" single or multiple candidates"
                  />
                </List>
              </Box>
            </Positions>

            <Stack sx={{ justifyContent: 'center' }} alignItems="center" pt={3}>
              <WsButton onClick={() => navigate('/email-settings/')}>Setup email account</WsButton>
            </Stack>
          </>
        </Box>
      ),
    },
    {
      component: (
        <Box>
          <>
            <Positions height={100}>
              <img src={NoJobsSVG} alt="No Jobs ?" height={130} width={140} />
              <Box>
                <List sx={{ width: '100%', maxWidth: '430', bgcolor: 'background.paper' }}>
                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    primary="3 reasons to Connect your Premium Candidate Databases to Wisestep."
                  />

                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    style={{ paddingTop: 10 }}
                    primary="1. Automatically Download all Viewed CV’s to your Talent Pool."
                  />

                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    primary="2.Track CV View Usage through our CV Views Dashboard ."
                  />

                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    primary="3.Allow your recruitment team to search Candidates Simultaneously ."
                  />
                </List>
              </Box>
            </Positions>
            <Stack sx={{ justifyContent: 'center' }} alignItems="center" pt={3}>
              <WsButton onClick={() => navigate('/native-search-settings/')}>Connect Now</WsButton>
            </Stack>
          </>
        </Box>
      ),
    },
    {
      component: (
        <Box>
          <>
            <Positions height={100}>
              <img src={NoCandidateIcon} alt="No Vendors ?" height={130} width={140} />
              <Box>
                <List sx={{ width: '100%', maxWidth: '430', bgcolor: 'background.paper' }}>
                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    primary="3 Reasons to connect your VMS (Vendor Management System) Logins to wisestep."
                  />

                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    style={{ paddingTop: 10 }}
                    primary="1.Real time sync for New Jobs Posted Integrates with 10 + popular VMS platforms."
                  />

                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    primary=" 2.Save hours of Manual Copy paste work for your teams."
                  />

                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    primary="3.Integrates with 10 + popular VMS platforms"
                  />
                </List>
              </Box>
            </Positions>
            <Stack sx={{ justifyContent: 'center' }} alignItems="center" pt={3}>
              <WsButton onClick={() => navigate('/vms')}>Connect your VMS</WsButton>
            </Stack>
          </>
        </Box>
      ),
    },
    {
      component: (
        <Positions height={150}>
          <img src={NoSmtpSVG} alt="No SMS Account ?" height={120} width={120} />
          <Box>
            <List sx={{ width: '100%', maxWidth: '430', bgcolor: 'background.paper' }}>
              <ListItemText
                primaryTypographyProps={{
                  fontWeight: 700,
                }}
                primary=" 3 reasons why you should setup your SMS Account on Wisestep."
              />

              <ListItemText
                primaryTypographyProps={{
                  fontWeight: 700,
                }}
                style={{ paddingTop: 10 }}
                primary="1. 95% of SMS’s are delivered to users as compared to 10% for Emails."
              />

              <ListItemText
                primaryTypographyProps={{
                  fontWeight: 700,
                }}
                primary=" 2.Candidates Respond to SMS within Minutes while it takes hours for Email responses."
              />

              <ListItemText
                primaryTypographyProps={{
                  fontWeight: 700,
                }}
                primary="3.Attractive Prices for our SMS Packages."
              />
            </List>
            <WsTypography bold>
              For more details contact our sales team on
              <Link
                underline="none"
                sx={{ color: WsColors.TheBlues900, ml: 0.6, mr: 0.6, fontWeight: 500 }}
                href="mailto:sales@wisestep-inc.com"
              >
                sales@wisestep-inc.com
              </Link>
            </WsTypography>
          </Box>
        </Positions>
      ),
    },
    {
      component: (
        <Box>
          <>
            <Positions height={100}>
              <img src={CalenderSVG} alt="No Calender Account ?" height={80} width={80} />

              <Box>
                <List sx={{ width: '100%', maxWidth: '430', bgcolor: 'background.paper' }}>
                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    primary=" 3 Reasons why you should configure up your Calendar account on Wisestep."
                  />

                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    style={{ paddingTop: 10 }}
                    primary=" 1.Integrate with Google or Microsoft Calendar."
                  />
                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    primary=" 2. Create Candidate Events from within Wisestep"
                  />
                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    primary=" 3.Manage your daily tasks efficiently"
                  />
                </List>
              </Box>
            </Positions>
            <Stack sx={{ justifyContent: 'center' }} alignItems="center" pt={3}>
              <WsButton onClick={() => navigate('/calender-settings/')}>
                Configure Calendar Account
              </WsButton>
            </Stack>
          </>
        </Box>
      ),
    },
    {
      component: (
        <Box>
          <>
            <Positions height={100}>
              <img src={DragandDropSVG} alt="No Templates ?" height={130} width={140} />

              <Box>
                <List sx={{ width: '100%', maxWidth: '430', bgcolor: 'background.paper' }}>
                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    primary="3 Reasons to Use email templates to send emails to multiple recipients in a single click."
                  />

                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    style={{ paddingTop: 10 }}
                    primary="1.Bulk Email Candidates with Jobs."
                  />

                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    primary="2.Email Clients with Candidate Submissions."
                  />
                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 700,
                    }}
                    primary="3.Create your own or Use the default ones."
                  />
                </List>
              </Box>
            </Positions>
            <Stack sx={{ justifyContent: 'center' }} alignItems="center" pt={3}>
              <WsButton onClick={() => navigate('/template-settings/email-template-settings')}>
                Create Templates
              </WsButton>
            </Stack>
          </>
        </Box>
      ),
    },
    {
      component: (
        <Box>
          <>
            <Positions height={100}>
              <img src={ExtensionchromeSVG} alt="Chrome Extension ?" height={60} width={60} />

              <Box>
                <WsTypography bold>
                  Add Wisestep extension in your chrome browser to automate the
                </WsTypography>
                <WsTypography bold>recruitment process and source the candidates</WsTypography>
              </Box>
            </Positions>
            <Stack sx={{ justifyContent: 'center' }} alignItems="center" pt={4}>
              <WsButton
                onClick={() =>
                  openUrlInNewTab(
                    'https://chrome.google.com/webstore/detail/recruitment-automation-by/dhbkmcoahmolllnkkpgniamjkldaemgm'
                  )
                }
              >
                Add Extension
              </WsButton>
            </Stack>
          </>
        </Box>
      ),
    },
  ];
  const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

  const theme = useTheme();
  const [selectedStep, setSelectedStep] = useState<number>(0);

  const handleNext = () => setSelectedStep((prevActiveStep) => prevActiveStep + 1);

  const handleBack = () => setSelectedStep((prevActiveStep) => prevActiveStep - 1);

  const handleStepChange = (step: number) => {
    onStepChange(step);
    setSelectedStep(step);
  };

  const maxSteps = options.length;

  return (
    <Grid container xs={12} alignItems={'center'}>
      <Box sx={{ maxWidth: '100%', flexGrow: 3, justifyContent: 'center' }}>
        <AutoPlaySwipeableViews
          axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
          index={selectedStep}
          onChangeIndex={handleStepChange}
          enableMouseEvents
        >
          {options.map((step, index) => (
            <div key={index}>
              {Math.abs(selectedStep - index) <= 2 ? (
                <Box
                  sx={{
                    maxHeight: 180,
                    display: 'block',
                    overflow: 'hidden',
                    maxWidth: '100%',
                    p: 2,
                  }}
                >
                  {step.component}
                </Box>
              ) : null}
            </div>
          ))}
        </AutoPlaySwipeableViews>
        <MobileStepper
          variant="dots"
          steps={maxSteps}
          position="static"
          activeStep={selectedStep}
          sx={{ minWidth: 40, flexGrow: 1, bgcolor: 'white' }}
          nextButton={
            <Button size="small" onClick={handleNext} disabled={selectedStep === maxSteps - 1}>
              Next
              {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </Button>
          }
          backButton={
            <Button size="small" onClick={handleBack} disabled={selectedStep === 0}>
              {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
              Back
            </Button>
          }
        />
      </Box>
    </Grid>
  );
};
